/**
 * QuikSigns — "e-signatures, without the extra cost" page.
 * All copy lives here; sections only handle layout and motion.
 */

import { LINKS } from "./quikit";

export const QSG = {
  meta: {
    title: "Electronic signatures & agreements — QuikSigns by Quikit",
    description:
      "Send documents, collect signatures, track progress and close agreements from one connected workspace — included with your Quikit product at no additional cost.",
  },
  links: { register: LINKS.register },
};

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */
export const QSG_HERO = {
  eyebrow: "E-signatures, without the extra cost",
  title: "Get agreements signed.\nKeep business moving.",
  sub: "Send documents, collect signatures, track progress and close agreements — all from one connected workspace, so contracts, proposals and approvals don’t move between disconnected tools.",
  lead: "QuikSigns is included with your Quikit product at no additional cost.",
  foot: "No credit card required. Up and running in minutes.",
  link: { label: "See QuikSigns in action", href: "#how" },
};

/** The signing envelope shown in the hero. */
export const QSG_PULSE = {
  path: "Workspace → Signs → Northwind MSA",
  doc: { name: "Northwind — master service agreement", meta: "sent 2 days ago · 3 signers · in order" },
  signers: [
    { name: "Priya Raman", role: "Northwind · Signer 1", state: "Signed" as const },
    { name: "Arun Mehta", role: "Legal · Signer 2", state: "Signed" as const },
    { name: "Dan Whitfield", role: "Northwind CFO · Signer 3", state: "Waiting" as const },
  ],
  reminder: { label: "Reminder queued", meta: "Dan · tomorrow 09:00" },
  result: { label: "Agreement complete", value: "3 of 3 signed" },
  linked: { label: "Filed to", items: ["QuikCRM · Northwind", "QuikDocs · Contracts"] },
  footer: "The signed agreement lands on the customer record, not in someone’s inbox.",
};

/* ------------------------------------------------------------------ */
/*  The problem                                                        */
/* ------------------------------------------------------------------ */
export const QSG_PROBLEM = {
  eyebrow: "The problem",
  title: "The signature is only one step. Everything around it creates the friction.",
  text: "Getting a contract signed usually means downloading files, switching tools, chasing people — then hunting for the signed copy later.",
  items: [
    {
      title: "Too many steps",
      text: "Prepare a document in one place, send it through another and store the signed version somewhere else again.",
    },
    {
      title: "Signature chasing",
      text: "A document goes out — but nobody knows whether it has been opened, reviewed or signed.",
    },
    {
      title: "Multiple signers",
      text: "When several people need to sign, keeping track of who has completed their part becomes difficult.",
    },
    {
      title: "Version confusion",
      text: "Different document versions circulate through email and messaging before the final agreement is completed.",
    },
    {
      title: "Manual follow-up",
      text: "Teams spend time sending reminders and checking status instead of moving the deal or process forward.",
    },
    {
      title: "Documents get disconnected",
      text: "A signed contract ends up in an inbox or folder far from the customer, project or employee it belongs to.",
    },
    {
      title: "Another subscription",
      text: "Businesses pay separately for e-signature software even though signing is already part of everyday workflows.",
    },
  ],
  result: {
    kicker: "The real problem",
    title: "The problem isn’t getting a signature. It’s keeping the whole agreement moving.",
    text: "A signature should move the business forward, not create another administrative task.",
    cta: "See how QuikSigns works",
  },
};

/* ------------------------------------------------------------------ */
/*  The shift                                                          */
/* ------------------------------------------------------------------ */
export const QSG_SHIFT = {
  eyebrow: "The shift",
  // Too long for the column on one line, so the break is placed rather than left to chance.
  title: "Stop chasing signatures.\nStart moving agreements forward.",
  text: "Traditional signing runs prepare, download, upload, send, chase, sign, store. QuikSigns collapses that into one connected workflow.",
  /** Each row is a genuine swap — no term appears on both sides of the arrow. */
  rows: [
    { from: "Download the file", to: "Prepare" },
    { from: "Upload it elsewhere", to: "Send" },
    { from: "Print and scan", to: "Sign" },
    { from: "Chase over email", to: "Track" },
    { from: "File it somewhere", to: "Connect" },
  ],
  questionsLead: "A signature should move the business forward, not create another administrative task. What your team actually needs to know:",
  questions: ["What was sent?", "What’s pending?", "What’s complete?", "What’s next?"],
};

/* ------------------------------------------------------------------ */
/*  How it works                                                       */
/* ------------------------------------------------------------------ */
export const QSG_STEPS = {
  eyebrow: "Meet QuikSigns",
  title: "Your everyday eSignature workspace, built into Quikit.",
  text: "Prepare documents, send them for signature, manage signers and track completion in one place.",
  steps: [
    {
      kicker: "Prepare",
      title: "Get the document ready to sign",
      text: "Upload the document and add the fields and signing details each recipient needs to complete.",
    },
    {
      kicker: "Send",
      title: "Straight to the people who need to act",
      text: "Send documents to the right recipients through a simple digital workflow — no downloading, re-uploading or attaching.",
    },
    {
      kicker: "Sign",
      title: "Easy for the person signing",
      text: "Recipients review and sign electronically from their own device, with no printing or scanning required.",
    },
    {
      kicker: "Track",
      title: "Know exactly where it stands",
      text: "See what has been sent, what is still pending and what has been completed, without asking anyone.",
    },
    {
      kicker: "Close",
      title: "Complete without the follow-up",
      text: "Move agreements toward completion with reminders doing the chasing instead of your team.",
    },
    {
      kicker: "Connect",
      title: "Filed where it belongs",
      text: "Keep signed documents close to the customer, project, employee or process the agreement is actually about.",
    },
  ],
  cta: "Start signing for free",
};

/* ------------------------------------------------------------------ */
/*  Everything you need                                                */
/* ------------------------------------------------------------------ */
export const QSG_EVERYTHING = {
  eyebrow: "Everything you need",
  title: "Simple signing. Powerful business workflow.",
  text: "The essential eSignature capabilities in one workspace, all included with your Quikit product.",
  items: [
    { icon: "doc", title: "Document preparation", text: "Upload documents and add the fields required for signing before you send." },
    { icon: "pen", title: "Electronic signatures", text: "A simple way for customers, employees and partners to sign digitally." },
    { icon: "user", title: "Multiple signers", text: "Send one document to several recipients when an agreement needs more than one signature." },
    { icon: "branch", title: "Signing order", text: "Set the sequence when a document must move through specific people in a defined order." },
    { icon: "target", title: "Document tracking", text: "See which documents are pending, completed or still waiting for action." },
    { icon: "bell", title: "Notifications & reminders", text: "Keep recipients aware of documents that still need their attention." },
    { icon: "lock", title: "Secure document access", text: "Keep signing activity and documents organized within your business workspace." },
    { icon: "refresh", title: "Connected workflows", text: "Connect signing activity to the broader business workflows across Quikit." },
  ],
  close: "One workspace. Every signature. Every agreement.",
};

/* ------------------------------------------------------------------ */
/*  From agreement to action                                           */
/* ------------------------------------------------------------------ */
export const QSG_OUTCOME = {
  eyebrow: "From agreement to action",
  title: "A signed document shouldn’t\nbe the end of the workflow.",
  text: "The real value of e-signatures begins after the signature. The signature is the milestone — the business action is the outcome.",
  items: [
    { title: "A signed sales agreement", text: "moves the deal into the next stage of the customer workflow." },
    { title: "A signed employment document", text: "moves onboarding forward for the new joiner." },
    { title: "A signed vendor agreement", text: "moves procurement past approval and into delivery." },
    { title: "A signed approval", text: "moves the project forward without another round of email." },
  ],
  close: "The signature is the milestone. The business action is the outcome.",
};

/* ------------------------------------------------------------------ */
/*  Document types                                                     */
/* ------------------------------------------------------------------ */
export const QSG_DOCUMENTS = {
  eyebrow: "Built for every business document",
  title: "If it needs a signature, QuikSigns can move it forward.",
  text: "From the first signature to the final agreement, everything stays in one workflow.",
  items: [
    { title: "Sales contracts", text: "Get customer agreements signed without slowing down the sales process." },
    { title: "Proposals", text: "Send proposals for review and signature from one workflow." },
    { title: "Employment documents", text: "Manage employee agreements and joining documentation digitally." },
    { title: "Vendor agreements", text: "Get supplier and partner documents signed without manual paperwork." },
    { title: "NDAs", text: "Send confidentiality agreements for quick digital signing." },
    { title: "Service agreements", text: "Move client service agreements from negotiation to completion." },
    { title: "Approvals", text: "Collect required approvals and signatures from stakeholders." },
    { title: "Project agreements", text: "Keep project-related contracts and approvals moving." },
    { title: "Business agreements", text: "Handle recurring agreements without printing, scanning or manual follow-up." },
  ],
  close: "From the first signature to the final agreement, keep everything moving.",
};

/* ------------------------------------------------------------------ */
/*  Roles                                                              */
/* ------------------------------------------------------------------ */
export type RoleKey = "sales" | "hr" | "finance" | "leadership";
export const QSG_ROLES = {
  eyebrow: "Built for the teams that send documents",
  title: "One workflow, four very different reasons to sign.",
  text: "Sales wants the deal closed. HR wants onboarding moving. Leadership wants to know where things stand.",
  tabs: [
    { key: "sales" as RoleKey, label: "Sales teams" },
    { key: "hr" as RoleKey, label: "HR teams" },
    { key: "finance" as RoleKey, label: "Finance & operations" },
    { key: "leadership" as RoleKey, label: "Leadership" },
  ],
  panels: {
    sales: {
      eyebrow: "For sales teams",
      title: "Close the deal without chasing the signature.",
      text: "A deal isn’t really closed until the agreement is signed. QuikSigns shrinks the gap between “they’re ready to sign” and “it’s done.”",
      items: [
        "Send contracts and proposals quickly",
        "Support agreements that need multiple signers",
        "Track progress while it’s still waiting",
        "Identify outstanding signatures instead of guessing",
        "Move completed agreements into the next stage",
        "From verbal yes to signed agreement",
      ],
      cta: "See it for my sales team",
    },
    hr: {
      eyebrow: "For HR teams",
      title: "Make employee documentation easier to complete.",
      text: "HR teams manage documents throughout the employee lifecycle. QuikSigns digitizes the signature step so onboarding doesn’t wait on paperwork.",
      items: [
        "Offer letters sent for signature",
        "Employment agreements collected digitally",
        "Policies and acknowledgements tracked",
        "HR documents kept organized",
        "Signed documentation close to the employee workflow",
        "Less paperwork, less chasing, smoother onboarding",
      ],
      cta: "See it for my HR team",
    },
    finance: {
      eyebrow: "For finance & operations",
      title: "Keep approvals and agreements moving.",
      text: "Finance and operations regularly handle documents that need review, approval and signature — usually through email chains that nobody can audit later.",
      items: [
        "Vendor agreements signed by suppliers and partners",
        "Purchase documents through defined signing workflows",
        "Service agreements kept organized",
        "Internal approvals without email chains",
        "Recurring signing workflows digitized",
        "Document-heavy processes become digital workflows",
      ],
      cta: "See it for my team",
    },
    leadership: {
      eyebrow: "For leadership teams",
      title: "Know which agreements are moving — and which aren’t.",
      text: "Leadership shouldn’t have to ask whether that contract has been signed yet. QuikSigns gives visibility into the signing process itself.",
      items: [
        "What was sent into the signing workflow",
        "What’s pending and waiting for action",
        "What’s been fully signed and completed",
        "What moves into the next business process",
        "Fewer bottlenecks hiding in inboxes",
        "Better visibility, faster decisions",
      ],
      cta: "Book a personalized demo",
    },
  },
};

/* ------------------------------------------------------------------ */
/*  The signing workflow                                               */
/* ------------------------------------------------------------------ */
export const QSG_WORKFLOW = {
  eyebrow: "Signers, tracking & collaboration",
  title: "One document. Multiple people. One clear workflow.",
  text: "Agreements involve customers, sales, managers, legal and partners. Getting them all through one document is the actual work.",
  blocks: [
    {
      kicker: "Multiple signers",
      title: "When one signature isn’t enough",
      text: "Bring everyone required into the same agreement, control who signs first and who follows, and know where the document sits in the sequence.",
      items: ["Add multiple signers", "Define the order", "Track each step", "Complete the agreement"],
      chain: true,
    },
    {
      kicker: "Tracking & reminders",
      title: "Stop asking, “did they sign it?”",
      text: "When agreements are sent, uncertainty creates delay. Know what needs attention before it becomes a bottleneck, with reminders doing the chasing.",
      items: ["Sent", "Pending", "Completed", "Reminders"],
    },
    {
      kicker: "Collaboration",
      title: "Keep the team aligned around every agreement",
      text: "Sales may own the customer, legal may review the agreement, finance may need visibility and leadership may approve it. The agreement shouldn’t disappear into one inbox.",
      items: ["Prepare together", "Review together", "Send confidently", "Track together"],
    },
  ],
  close: "Know what needs attention before it becomes a bottleneck.",
};

/* ------------------------------------------------------------------ */
/*  Connected to your business                                         */
/* ------------------------------------------------------------------ */
export const QSG_ECOSYSTEM = {
  eyebrow: "Connected to your business",
  title: "Your signed document should stay with the work it belongs to.",
  text: "A customer agreement belongs with the customer. A signed contract shouldn’t become a file nobody can find.",
  groups: [
    { name: "QuikCRM", items: ["Customer agreements", "Sales workflows", "Account records"] },
    { name: "QuikPeople", items: ["Employee documentation", "HR workflows", "Onboarding"] },
    { name: "QuikFinance", items: ["Financial agreements", "Approvals", "Finance workflows"] },
    { name: "QuikProjects", items: ["Project agreements", "Approvals", "Project execution"] },
    { name: "QuikDocs", items: ["Prepare the document", "Manage versions", "Before signing"] },
    { name: "QuikForms", items: ["Collect information first", "Then move to signature", "One intake flow"] },
    { name: "QuikSheets", items: ["Agreement data", "Tracking information", "Structured reporting"] },
    { name: "QuikInsights", items: ["Agreement analysis", "Workflow information", "Business reporting"] },
  ],
  close: "The document gets signed in QuikSigns. The business keeps moving everywhere else.",
};

/* ------------------------------------------------------------------ */
/*  Why QuikSigns                                                      */
/* ------------------------------------------------------------------ */
export const QSG_WHY = {
  eyebrow: "Why QuikSigns",
  title: "E-signatures are simple. Keeping every agreement moving is the difference.",
  text: "One workspace. Every employee. Every signature.",
  items: [
    { title: "Sign faster", text: "Move beyond printing, scanning and chasing signatures around by email." },
    { title: "Send with ease", text: "Get documents to the right people through a simple digital workflow." },
    { title: "Support multiple signers", text: "Manage agreements that require several people to sign." },
    { title: "Control signing order", text: "Define exactly who signs and when the document reaches them." },
    { title: "Track every agreement", text: "Know what is pending, completed or still needs attention." },
    { title: "Keep documents organized", text: "Keep signing activity and documents in one accessible workspace." },
    { title: "Work where you already work", text: "Keep signatures connected to the broader Quikit ecosystem." },
    { title: "No extra subscription", text: "QuikSigns is included with your Quikit product at no additional cost." },
  ],
};

/* ------------------------------------------------------------------ */
/*  Compare                                                            */
/* ------------------------------------------------------------------ */
export type CompareCell = boolean | string;
export const QSG_COMPARE = {
  eyebrow: "QuikSigns vs standalone eSignature tools",
  title: "Why add another subscription\njust to get a document signed?",
  text: "The signing itself is table stakes. What differs is where the signed agreement ends up — and whether you pay separately for the privilege.",
  columns: ["QuikSigns", "Standalone tool"],
  rows: [
    { feature: "Electronic signatures", cells: [true, true] as CompareCell[] },
    { feature: "Document preparation", cells: [true, true] as CompareCell[] },
    { feature: "Multiple signers", cells: [true, true] as CompareCell[] },
    { feature: "Signing order", cells: [true, "Varies"] as CompareCell[] },
    { feature: "Document tracking", cells: [true, true] as CompareCell[] },
    { feature: "Notifications & reminders", cells: [true, true] as CompareCell[] },
    { feature: "Secure document access", cells: [true, "Varies"] as CompareCell[] },
    { feature: "Business ecosystem", cells: ["Quikit", "Standalone"] as CompareCell[] },
    { feature: "Quikit integration", cells: [true, false] as CompareCell[] },
    { feature: "Connected workflows", cells: [true, "Limited"] as CompareCell[] },
    { feature: "Additional subscription", cells: ["$0", "Required"] as CompareCell[] },
  ],
  close: {
    title: "Two ways to get a document signed.",
    options: [{ name: "A standalone eSignature tool", text: "Helps you get documents signed — then hands the file back for someone to file away." }],
    product: { name: "QuikSigns", text: "Helps you get documents signed as part of the business workflow they belong to." },
  },
};

/* ------------------------------------------------------------------ */
/*  The business case                                                  */
/* ------------------------------------------------------------------ */
export const QSG_CASE = {
  eyebrow: "The business case",
  title: "Why pay separately for something your business already needs?",
  text: "Every business sends documents and signs agreements. If you already use Quikit, that shouldn’t need another subscription.",
  manual: {
    kicker: "A separate eSignature subscription",
    title: "Another licence, and a gap on either side of it",
    items: ["Its own bill", "Its own document store", "Manual upload", "Manual filing after", "No business context", "Another place to look"],
    value: "Separate",
    unit: "recurring subscription",
  },
  product: {
    kicker: "QuikSigns",
    title: "Included with any Quikit product",
    text: "Document preparation, electronic signatures, multiple signers, signing order, tracking, reminders and secure access are built into the ecosystem rather than sold alongside it.",
    close: "One less subscription. One more connected workflow.",
    value: "$0",
    unit: "additional per user",
  },
  /** The four figures come from the brief's own reported numbers, presented as reported. */
  proof: {
    label: "Reported on the QuikSigns product page",
    stats: [
      { value: "10K+", text: "documents signed" },
      { value: "2.5K+", text: "active teams" },
      { value: "50K+", text: "signatures completed" },
      { value: "$0", text: "additional cost with Quikit" },
    ],
    note: "Product-reported adoption figures. Results and usage vary by organization.",
  },
};

/* ------------------------------------------------------------------ */
/*  Proof                                                              */
/* ------------------------------------------------------------------ */
export type ProofStory = { quote: string; name: string; role: string };
/**
 * The brief supplies three testimonials but asks that they be used only once approved, and
 * flags that the live page repeats the same proof more than once. `stories` therefore ships
 * empty and the fallback runs instead. Add each approved quote here exactly once — the
 * section renders whatever is in the array, so a duplicate would show as a duplicate.
 */
export const QSG_PROOF = {
  eyebrow: "Trusted by connected teams",
  title: "Stop paying per signature workflow.",
  stories: [] as ProofStory[],
  fallback: {
    text: "See the signing workflow itself — preparing a document, routing it through several signers and filing the completed agreement.",
    items: [
      { title: "A walkthrough of the real product", text: "Every screen, using one of your own agreements as the example." },
      { title: "A multi-signer document, end to end", text: "Sent, signed in sequence, chased by reminder and completed." },
      { title: "The signed file, already filed", text: "Landing on the customer or employee record rather than an inbox." },
    ],
    cta: "See how QuikSigns works",
  },
};

/* ------------------------------------------------------------------ */
/*  Plans                                                              */
/* ------------------------------------------------------------------ */
/** Every figure here comes from the brief — the trial, $0 included, the $1 add-on and Enterprise. */
export const QSG_PLANS = {
  eyebrow: "Pricing",
  title: "Professional eSignatures. Included with Quikit.",
  text: "No separate eSignature subscription. No additional per-user fee.",
  plans: [
    {
      name: "Free",
      price: "$0",
      unit: "for 14 days",
      text: "Experience the full QuikSigns product before you commit, with every feature unlocked.",
      lead: "",
      cta: "Start free trial",
      ctaHref: LINKS.register,
      foot: "No credit card required",
      features: [
        "Every QuikSigns feature unlocked",
        "Document preparation",
        "Electronic signatures",
        "Multiple signers",
        "Signing order",
        "Document tracking",
        "Notifications & reminders",
        "Secure document access",
        "Community support",
      ],
    },
    {
      name: "Premium",
      price: "$0",
      unit: "included with Quikit",
      text: "Included with any Quikit product — no separate eSignature subscription.",
      lead: "Everything in Free, plus:",
      featured: true,
      badge: "Included",
      cta: "Get QuikSigns",
      ctaHref: LINKS.register,
      foot: "No separate eSignature licence",
      features: [
        "Connections with Quikit products",
        "Available across your organization",
        "Signed documents filed to the record",
        "Full REST API",
        "Email & chat support",
      ],
    },
    {
      name: "Pro",
      price: "$1",
      unit: "/ product / user / month",
      text: "Add additional Quikit products to the same workspace as your business grows.",
      lead: "Everything in Premium, plus:",
      cta: "Explore Quikit",
      ctaHref: "/#pricing",
      foot: "Add or remove products as you grow",
      features: [
        "One login across products",
        "Shared data",
        "One AI layer",
        "Add or remove products as you grow",
        "Email & chat support",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      unit: "",
      text: "For larger organizations and bespoke requirements.",
      lead: "",
      cta: "Talk to sales",
      ctaHref: "#demo",
      foot: "Tailored to your organization",
      features: [
        "Dedicated account manager",
        "Migration & onboarding support",
        "Custom deployment options",
        "Priority support",
        "API & integration support",
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */
export const QSG_FAQ = [
  {
    q: "What is QuikSigns?",
    a: "QuikSigns is Quikit’s electronic signature and document signing workspace. It helps businesses prepare, send, sign and track documents digitally.",
  },
  {
    q: "Is QuikSigns free?",
    a: "Yes. QuikSigns is included at no additional cost with any Quikit product.",
  },
  {
    q: "Do I need a separate eSignature subscription?",
    a: "No. QuikSigns is included with your Quikit product.",
  },
  {
    q: "What types of documents can I sign?",
    a: "Contracts, agreements, proposals, employment documents, vendor agreements, approvals and other business documents that require signatures.",
  },
  {
    q: "Can multiple people sign the same document?",
    a: "Yes. QuikSigns supports workflows where several recipients need to sign the same document.",
  },
  {
    q: "Can I control the signing order?",
    a: "Yes. Defined signing sequences let a document move through multiple people in a particular order.",
  },
  {
    q: "Can I track document status?",
    a: "Yes. You can track documents through their signing workflow, including what is pending and what is completed.",
  },
  {
    q: "Does QuikSigns send reminders?",
    a: "Yes. Notifications and reminders help keep outstanding signatures moving without manual chasing.",
  },
  {
    q: "Do recipients need to print the document?",
    a: "No. QuikSigns is designed for electronic signing without printing and scanning.",
  },
  {
    q: "Can QuikSigns work with other Quikit products?",
    a: "Yes. QuikSigns is part of the wider Quikit ecosystem and works alongside other Quikit applications.",
  },
  {
    q: "Can I use QuikSigns for sales contracts?",
    a: "Yes. Sales teams can use it for contracts, proposals and other customer agreements that require signatures.",
  },
  {
    q: "Can HR teams use QuikSigns?",
    a: "Yes. HR teams use it for employment documents, acknowledgements, agreements and other employee paperwork.",
  },
  {
    q: "Can I use QuikSigns for vendor agreements?",
    a: "Yes. Vendor and partner agreements are among the business documents QuikSigns supports.",
  },
  {
    q: "Is there an API?",
    a: "Yes. A full REST API is part of the product.",
  },
  {
    q: "How is QuikSigns different from a standalone eSignature tool?",
    a: "QuikSigns combines electronic signing with the wider Quikit business ecosystem, and is included with any Quikit product rather than requiring another separate subscription.",
  },
];

/* ------------------------------------------------------------------ */
/*  Demo                                                               */
/* ------------------------------------------------------------------ */
export type QsgIntent = "consolidate" | "close" | "evaluate";
export const QSG_DEMO = {
  agenda: [
    { title: "An agreement, end to end", text: "Prepared, routed through three signers in order and completed." },
    { title: "Where the signed file lands", text: "Filed against the customer or employee record, not an inbox." },
    { title: "The subscription you drop", text: "What your eSignature tool costs today against $0 with Quikit." },
  ],
  intents: [
    { value: "consolidate" as QsgIntent, label: "Drop our eSign tool", meta: "We pay for one today" },
    { value: "close" as QsgIntent, label: "Close deals faster", meta: "Signatures hold us up" },
    { value: "evaluate" as QsgIntent, label: "Just evaluating", meta: "Exploring options" },
  ],
  copy: {
    consolidate: {
      eyebrow: "Drop the extra subscription",
      title: "Let’s replace your eSignature tool.",
      lead: "Tell us what your team uses today and how many documents you send. We’ll show you what QuikSigns covers at no additional cost.",
      button: "Show me the comparison",
      ok: "Request received",
      msg: "A QuikSigns specialist will contact you with the comparison.",
    },
    close: {
      eyebrow: "Close deals faster",
      title: "Let’s shorten the signing gap.",
      lead: "Tell us how your agreements get signed today and where they stall. We’ll show you the workflow QuikSigns would put around them.",
      button: "Shorten my signing cycle",
      ok: "Request received",
      msg: "A QuikSigns specialist will contact you about your signing workflow.",
    },
    evaluate: {
      eyebrow: "Evaluate QuikSigns",
      title: "Comparing eSignature options?",
      lead: "Tell us how documents get signed today — an eSignature tool, printing and scanning, or email attachments. We’ll show you how QuikSigns would fit.",
      button: "Book my walkthrough",
      ok: "Request received",
      msg: "A QuikSigns specialist will contact you to set up your walkthrough.",
    },
  } satisfies Record<QsgIntent, { eyebrow: string; title: string; lead: string; button: string; ok: string; msg: string }>,
  sizes: ["1–10", "11–50", "51–200", "201–500", "500+"],
  current: ["DocuSign", "Adobe Acrobat Sign", "Zoho Sign", "Print and scan", "Email attachments", "Nothing formal yet"],
};

/* ------------------------------------------------------------------ */
/*  Closing                                                            */
/* ------------------------------------------------------------------ */
export const QSG_CTA = {
  eyebrow: "Get documents signed",
  title: "Then get back to business.",
  text: "Prepare the document, collect every signature, track what needs attention — and keep the signed agreement connected to the work.",
  steps: ["Prepare", "Send", "Sign", "Track", "Close", "Act"],
  foot: "No credit card required. Included with your Quikit product.",
};
